import { Injectable } from '@angular/core';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { isCapacitorNative, isPlatformIOS } from 'environments/environment-helpers';
import { CloudFunctionsService } from './cloud-functions.service';
import { AuthService } from './auth.service';
import { CouchLoginReason } from './couch-login-storm';
import { LoggingService } from './logging.service';
import { CoachTierValidateParams, CoachTierValidateResult } from '@cf-shared/coach-billing-interface';
import { SubscriptionStatus } from '@cf-shared/data-model/couch-user';
import { isInGrace } from '@cf-shared/data-model/license-reconciler';
import { DateTime } from 'luxon';
import { describeError } from '@cf-shared/error-describe';
import { isStoreCouldNotCheckCode } from '@cf-shared/store-validate-errors';
import { runStorePurchaseAsync, StorePurchaseResponse } from './store-purchase-flow';
import { describeRefusalReport, describeThrownReport, StorePurchaseFailureReport, StorePurchaseRail } from './store-purchase-failure-report';
import { pickPersonalRestorePurchase } from './store-restore-pick';
import { isNetworkError } from './cloud-functions-errors';
import { AppEnvironment } from 'environments/environment';
import {
  forgetCoachTierPendingPurchase,
  forgetCoachTierPendingPurchaseIfSettled,
  rememberCoachTierPurchase,
} from './coach-tier-pending-purchase';

// Capacitor plugin bridge — resolves to native BillingPlugin on Android/iOS, no-op on web.
// Exported so `subscription-strategy.ts` reuses the same proxy instead of calling
// `registerPlugin('BillingPlugin')` a second time (which would materialize a duplicate
// proxy object for the same native plugin name).
export interface BillingPluginInterface {
  getProducts(options: { productIds: string }): Promise<{ products: BillingProduct[] }>;
  purchase(options: { productId: string; offerToken: string; accountId?: string }): Promise<StorePurchaseResponse>;
  updateSubscription(options: { productId: string; offerToken: string; oldPurchaseToken: string; accountId?: string }): Promise<StorePurchaseResponse>;
  getExistingPurchases(): Promise<{ purchases: BillingPurchase[] }>;
  acknowledgePurchase(options: { purchaseToken: string }): Promise<void>;
  // iOS only — presents StoreKit's native Manage Subscriptions sheet. Not implemented on
  // Android; callers must gate on isPlatformIOS() (see IAPService.showManageSubscriptionsAsync),
  // since a direct call on Android would reject "not implemented".
  showManageSubscriptions(): Promise<void>;
}

interface BillingProduct {
  id: string;
  title: string;
  price: string; // formatted price string from Google Play / StoreKit
  priceMicros: number;
  currencyCode: string;
  // StoreKit's storefront locale (e.g. "en_US"), exposed by the iOS bridge so
  // the JS side can format derived prices (savings, pre-discount) in the same
  // locale as `price`. Absent on Android — caller falls back to navigator.language.
  priceLocale?: string;
  billingPeriod: string; // ISO 8601 duration: P1M, P1Y
  offerToken: string;
  basePlanId: string;
  offerId?: string;
  trialPeriod?: string;
  trialPriceMicros?: number;
}

interface BillingPurchase {
  purchaseToken: string;
  orderId: string;
  productId: string;
  isAcknowledged: boolean;
  /** iOS only: StoreKit's signed copy of the transaction — see
   *  `StorePurchaseResponse.signedTransaction` (#5871). */
  signedTransaction?: string;
}

// provider-agnostic product info for the subscribe page
export interface IAPProduct {
  id: string;
  title: string;
  priceText: string;
  priceMicros: number;
  currencyCode: string;
  // storefront locale, used to keep derived savings/pre-discount strings in
  // the same format as priceText. Optional because the Android Billing
  // library doesn't expose an equivalent; callers fall back to
  // navigator.language when absent.
  priceLocale?: string;
  numMonths: number;
  offerToken: string;
  basePlanId: string;
  offerId?: string;
  hasTrial: boolean;
  trialPeriod?: string;
  saving?: string;
  preDiscount?: string;
}

export interface IAPPurchaseResult {
  success: boolean;
  canceled?: boolean;
  error?: string;
  linkedEmail?: string;
  /** True when the store reported a completed, paid transaction and the failure
   *  recorded in `error` happened AFTER that, in our own confirmation step. The
   *  buyer has been charged and the purchase is not lost, so nothing shown to
   *  them may say the purchase failed (#4651).
   *
   *  Left unset when the store SETTLED the question — an expired subscription,
   *  or one owned by another account — because there the purchase really is not
   *  usable and the terminal wording is correct. See
   *  `design-docs/iap-subscriptions.md`, "Could not check is not the same as
   *  not valid". */
  confirmationFailedAfterPurchase?: boolean;
  /** True when the failure in `error` came from a throw that this service has
   *  already reported to Sentry. A caller that would report a failed result
   *  must skip it then, or one failure becomes two Sentry problems, the second
   *  without the error's name or stack (df-d2afc9172013). Left unset for every
   *  store answer, which nothing here has reported. */
  alreadyReported?: boolean;
}

/** The failure answer for a store purchase, plan change or tier change our
 *  server could not confirm.
 *
 *  Every path that asks a store-validate endpoint builds its failure answer
 *  here, so the one decision any of them makes about a buyer's money — was this
 *  a store that could not answer, or a store that settled the question? — is
 *  written once. Spread across the call sites it would be one copy of the same
 *  comparison per store per path, and a later change to what counts as "could
 *  not check" that missed one would silently go back to telling that store's
 *  buyers their purchase failed. That is not hypothetical: it is exactly what
 *  happened to the five paths #4651 did not reach.
 *
 *  **Call it only past the `purchaseToken` guard**, which is the point from
 *  which the store has completed and charged the transaction. Earlier than that
 *  the flag would promise an activation to somebody who was never charged.
 *
 *  It is deliberately NOT typed as `IAPPurchaseResult`. The plan-switch and
 *  coach-tier paths return their own result types, and the object below is
 *  structurally assignable to all three — so one builder serves them all
 *  without a cast and without any of them depending on the others' shape. */
export function buildStoreConfirmationFailure(
  validation: { error?: string; linkedEmail?: string },
) {
  return {
    success: false,
    error: validation.error,
    linkedEmail: validation.linkedEmail,
    confirmationFailedAfterPurchase: isStoreCouldNotCheckCode(validation.error),
  };
}

export const BillingPlugin = registerPlugin<BillingPluginInterface>('BillingPlugin');

@Injectable()
export class IAPService {

  // callbacks registered via onStorefrontChanged(); invoked when StoreKit
  // emits a storefront-update event from the native side.
  private storefrontChangedCallbacks = new Set<() => void>();

  constructor(
    private cloudFunctions: CloudFunctionsService,
    private auth: AuthService,
    private logging: LoggingService,
  ) {
    // iOS-only — Android billing has no equivalent storefront-change concept,
    // and BillingPlugin.addListener('storefrontChanged', …) is a no-op on the
    // Android side. Skip the attach to avoid an unnecessary bridge call.
    if (this.isAvailable() && isPlatformIOS()) {
      void this.attachStorefrontListenerAsync();
    }
  }

  isAvailable(): boolean {
    return isCapacitorNative();
  }

  private async attachStorefrontListenerAsync(): Promise<void> {
    try {
      // listener handle intentionally not retained — service is an
      // app-singleton with the same lifetime as the plugin, so there's
      // nothing to detach later.
      await (BillingPlugin as any).addListener(
        'storefrontChanged',
        (data: { storefrontId: string; countryCode: string }) => {
          console.log('[iap.service] StoreKit storefront changed:', data);
          for (const cb of this.storefrontChangedCallbacks) {
            try {
              cb();
            } catch (err) {
              this.logging.trackSilentProblem('storefrontChangedCallbackError', {
                errorMessage: describeError(err),
              });
            }
          }
        },
      );
    } catch (err) {
      // failing to attach the listener is non-fatal — the page can still
      // render with whatever the initial getProducts returned. Surface
      // so a future iOS update that breaks the bridge is visible.
      this.logging.trackSilentProblem('storefrontListenerAttachError', {
        errorMessage: describeError(err),
      });
    }
  }

  /**
   * Register a callback that fires whenever StoreKit resolves a new
   * storefront for the user (sandbox tester sign-in, region change in
   * Settings, etc.). Returns an unsubscribe function — callers must
   * invoke it on page teardown to prevent the callback from firing
   * after the host page is gone.
   *
   * Used by the Subscribe page to re-fetch products so the displayed
   * price matches what Apple's purchase sheet will charge.
   */
  onStorefrontChanged(cb: () => void): () => void {
    this.storefrontChangedCallbacks.add(cb);
    return () => { this.storefrontChangedCallbacks.delete(cb); };
  }

  /**
   * Fetch products and, on iOS, wait briefly for a possible
   * `Storefront.updates` event before returning. If the storefront
   * changes during the settle window, re-fetch and return the new
   * prices instead of the initial ones. Eliminates the "render USD
   * then flicker to EUR" UX when StoreKit resolves the user's actual
   * sandbox-tester storefront slightly after the first product query.
   *
   * On Android / web the settle window is skipped — `Storefront.updates`
   * is iOS-only — so the call collapses to `getProductsAsync`.
   *
   * The settle delay only fires on the FIRST page-render; subsequent
   * storefront changes still propagate via the persistent listener
   * registered by `onStorefrontChanged`.
   */
  async getProductsWithSettledStorefrontAsync(
    productIds: string[],
    settleMs = 1500,
  ): Promise<IAPProduct[]> {
    if (!this.isAvailable() || !isPlatformIOS()) {
      return this.getProductsAsync(productIds);
    }

    let storefrontChangedDuringFetch = false;
    const unsubscribe = this.onStorefrontChanged(() => {
      storefrontChangedDuringFetch = true;
    });

    try {
      // Initial fetch + settle delay run in parallel. The fetch usually
      // resolves well within `settleMs`; the wait gives StoreKit a chance
      // to fire `Storefront.updates` if the storefront is going to be
      // late-resolved (the sandbox scenario described above).
      const initialPromise = this.getProductsAsync(productIds);
      const settlePromise = new Promise<void>(resolve => setTimeout(resolve, settleMs));
      const [initial] = await Promise.all([initialPromise, settlePromise]);

      if (storefrontChangedDuringFetch) {
        console.log('[iap.service] Storefront changed during initial fetch — re-fetching');
        return await this.getProductsAsync(productIds);
      }

      return initial;
    } finally {
      unsubscribe();
    }
  }

  async getProductsAsync(productIds: string[]): Promise<IAPProduct[]> {
    if (!this.isAvailable()) return [];

    const result = await BillingPlugin.getProducts({ productIds: productIds.join(',') });
    const allProducts: IAPProduct[] = result.products.map(p => ({
      id: p.id,
      title: p.title,
      priceText: p.price,
      priceMicros: p.priceMicros,
      currencyCode: p.currencyCode,
      priceLocale: p.priceLocale,
      numMonths: billingPeriodToMonths(p.billingPeriod),
      offerToken: p.offerToken,
      basePlanId: p.basePlanId,
      offerId: p.offerId,
      hasTrial: !!p.trialPeriod,
      trialPeriod: p.trialPeriod,
    }));

    // deduplicate: for each basePlanId, prefer the trial offer over the base plan
    const byBasePlan = new Map<string, IAPProduct>();
    for (const p of allProducts) {
      const existing = byBasePlan.get(p.basePlanId);
      if (!existing || (p.hasTrial && !existing.hasTrial)) {
        byBasePlan.set(p.basePlanId, p);
      }
    }
    const products = Array.from(byBasePlan.values());

    // calculate savings for yearly vs monthly
    this.calculateSavings(products);

    return products;
  }

  /**
   * Purchase a PERSONAL Premium subscription.
   *
   * The order of steps, and every decision about the buyer's money, lives in
   * `runStorePurchaseAsync` — see `store-purchase-flow.ts` for why. What is
   * supplied below is only what differs from the coach-tier purchase: which
   * store call to make, what an already-owned answer means here, which validate
   * endpoint confirms it, and the Sentry names this path reports under.
   */
  async purchaseAsync(product: IAPProduct): Promise<IAPPurchaseResult> {
    return runStorePurchaseAsync({
      isAvailable: () => this.isAvailable(),
      getAccountIdAsync: () => this.getObfuscatedAccountId(),

      runStoreCallAsync: accountId => BillingPlugin.purchase({
        productId: product.id,
        offerToken: product.offerToken,
        accountId,
      }),

      // staleTransaction:true means StoreKit short-circuited product.purchase()
      // to a lingering expired/revoked entitlement instead of creating a fresh
      // purchase (#3470). The native side now clears unfinished expired
      // transactions before purchasing and finishes renewals via a
      // Transaction.updates listener, so this should be rare; if it still
      // fires, the flow treats it as a (silent) cancel and we leave a Sentry
      // trail here.
      reportStaleTransaction: response => {
        this.logging.trackSilentProblem('NativePurchaseStaleTransaction', { productId: product.id, reason: response.staleReason });
      },

      // the store charged, but the device could not verify the receipt's
      // signature. The server still confirms it with the store; this leaves a
      // Sentry trail so these can be counted (df-e8d8826c8191).
      reportUnverifiedOnDevice: response => {
        this.logging.trackSilentProblem('NativePurchaseUnverifiedOnDevice', { productId: product.id, reason: response.unverifiedReason });
      },

      // if user already owns the subscription, restore it
      onAlreadyOwnedAsync: () => this.restorePurchasesAsync(),

      // Nothing to write down, and nothing to forget. This rail already recovers
      // an unconfirmed purchase twice over: `checkExistingStorePurchase` re-runs
      // the confirmation every time the Subscribe page loads, and `couchLogin`
      // re-validates the personal subscription block against the store. The
      // coach-tier rail has neither, which is why it keeps a record here.
      rememberUnconfirmedPurchase: () => {},
      onServerAnswered: () => {},

      // validate with server and activate subscription
      validateAsync: (response, accountId) => Capacitor.getPlatform() === 'ios'
        ? this.cloudFunctions.appleValidateAsync({
            transactionId: response.purchaseToken || '',
            originalTransactionId: response.orderId || response.purchaseToken || '',
            signedTransaction: response.signedTransaction,
          })
        : this.cloudFunctions.googlePlayValidateAsync({
            purchaseToken: response.purchaseToken,
            productId: response.productId || product.id,
            obfuscatedAccountId: accountId,
          }),

      // server rejected a completed store purchase — the (previously silent)
      // path behind the user-facing "purchase failed" dialog and the original
      // Apple-Review rejection. Leave a Sentry trail.
      reportValidateFailed: validation => {
        this.reportPurchaseFailure(describeRefusalReport(StorePurchaseRail.Personal, validation.error));
      },
      buildValidationFailure: validation => buildStoreConfirmationFailure(validation),

      reportThrew: (error, step) => {
        this.reportPurchaseFailure(describeThrownReport(StorePurchaseRail.Personal, error, step));
      },
      describeThrown: error => describeError(error),

      // re-login to refresh subscription status in the app
      finishAsync: () => this.auth.attemptCouchLoginAsync(CouchLoginReason.StorePurchaseFinish),
    });
  }

  /** Send one store-purchase failure to Sentry under the name and tags
   *  `store-purchase-failure-report.ts` chose for it (#6244). */
  private reportPurchaseFailure(report: StorePurchaseFailureReport) {
    this.logging.trackSilentProblem(report.name, report.context, report.tags);
  }

  /**
   * The purchase token of the user's currently-granting personal Google
   * Play subscription — Active OR in-grace/Suspended (account-hold), the
   * same `isInGrace` notion the server's cross-provider gate uses — when
   * one exists and should be replaced by a coach-tier purchase to avoid
   * double billing. Android-only — iOS can't replace across subscription
   * groups. Returns undefined when there's no access-granting personal
   * Google sub (a free user becoming a coach has nothing to replace, so it
   * stays a plain purchase).
   */
  private activeGooglePlayPersonalTokenForReplacement(): string | undefined {
    if (Capacitor.getPlatform() !== 'android') return undefined;
    const gp = this.auth.couch?.googlePlayData;
    // gate on the SAME in-grace notion the server's cross-provider check uses
    // (isInGrace), not a strict `=== Active`: a personal Google sub in a
    // grace / account-hold state (Suspended, still non-expired) still grants
    // Premium and can still resume billing, so it must ALSO be replaced.
    // A strict-Active check here would let a become-a-coach plain purchase run
    // over a Suspended personal sub, leaving two active-granting Google blocks
    // and a double bill — the exact state this coordination prevents (the
    // cancel-RTDN fallback never fires because no buy-as-replacement ran). See
    // design-docs/coach-subscription-coordination.md §4.
    if (gp?.purchaseToken && isInGrace(gp.subscriptionStatus ?? SubscriptionStatus.None, gp.expiresDate_utc, DateTime.utc().toMillis())) {
      return gp.purchaseToken;
    }
    return undefined;
  }

  /**
   * Purchase a COACH-TIER subscription (Phase 2 fixed-tier rail). This is
   * the coach-tier counterpart to `purchaseAsync`: it runs the native
   * store purchase, but validates the receipt through `coachTierValidate`
   * (which writes the `coachTierData` block) instead of the personal
   * `appleValidate` / `googlePlayValidate`. Both run the same steps in the
   * same order, and that order lives in `runStorePurchaseAsync` — see
   * `store-purchase-flow.ts`. What is supplied below is only what differs.
   *
   * Double-bill avoidance on Google Play: a coach-tier sub also grants the
   * coach personal Premium, so a user who already pays for personal
   * Premium must not be left paying for both. Google allows multiple
   * concurrent subscriptions per app, and a plain purchase would stack
   * them — so when the user has an access-granting (Active or in-grace)
   * personal Google Play sub we run
   * the buy as a replacement (`updateSubscription` with the personal sub's
   * `oldPurchaseToken`), which cancels personal Premium as the coach tier
   * takes over. iOS can't replace across subscription groups, so it stays
   * a plain purchase (and the coach rail is gated off on iOS until its
   * products exist anyway).
   */
  async purchaseCoachTierAsync(product: IAPProduct): Promise<IAPPurchaseResult> {
    // decided when the store call is made and read again when the purchase is
    // validated, so the two cannot disagree about whether this buy replaced a
    // personal subscription. Held here rather than computed twice because the
    // answer depends on the user document, which a re-login could change.
    let replacePersonalToken: string | undefined;

    return runStorePurchaseAsync<CoachTierValidateResult>({
      isAvailable: () => this.isAvailable(),
      getAccountIdAsync: () => this.getObfuscatedAccountId(),

      runStoreCallAsync: accountId => {
        // if the user has an active personal Google Play sub, replace it so
        // they aren't billed for both personal Premium and the coach tier
        replacePersonalToken = this.activeGooglePlayPersonalTokenForReplacement();
        return replacePersonalToken
          ? BillingPlugin.updateSubscription({
              productId: product.id,
              offerToken: product.offerToken,
              oldPurchaseToken: replacePersonalToken,
              accountId,
            })
          : BillingPlugin.purchase({
              productId: product.id,
              offerToken: product.offerToken,
              accountId,
            });
      },

      reportStaleTransaction: response => {
        this.logging.trackSilentProblem('CoachTierPurchaseStaleTransaction', { productId: product.id, reason: response.staleReason });
      },

      reportUnverifiedOnDevice: response => {
        this.logging.trackSilentProblem('CoachTierPurchaseUnverifiedOnDevice', { productId: product.id, reason: response.unverifiedReason });
      },

      // The store says this account already holds this plan. That is the answer a
      // coach gets when an earlier purchase of theirs was never recorded — so
      // before treating it as a mistake, ask the store what they own and finish
      // the job from that. A coach who really does hold a recorded tier still
      // gets the old answer, which sends them to Change Plan.
      onAlreadyOwnedAsync: () => this.confirmOwnedCoachTierAsync(product),

      // write the purchase down on the device before our server is told anything.
      // The coach-tier rail has no other way back if the call below throws: the
      // server parks nothing it never heard of, a sign-in re-checks only the
      // personal blocks, and the store's own notification matches a coach-tier
      // token this coach does not have yet (`df-2745c2d64a48`).
      rememberUnconfirmedPurchase: (response, accountId) => {
        const uid = this.auth.user?.uid;
        if (!uid) return; // nothing to attach it to; the retry refuses it anyway
        const params = this.buildCoachTierPurchaseParams(response, accountId, product, replacePersonalToken);
        const remembered = rememberCoachTierPurchase({ uid, rememberedAt_utc: DateTime.utc().toISO()!, params });
        if (!remembered.stored) {
          // the record is the recovery, not the purchase — losing it must not
          // stop the confirmation, but we want to know it happened, because a
          // coach on this route silently loses the only thing that recovers them.
          this.logging.trackSilentProblem('CoachTierPendingPurchaseNotStored', { provider: params.provider, errorMessage: describeError(remembered.error) });
        }
      },

      // our server has answered, so the device record has done its job — unless
      // the answer was one where nobody has settled anything and nothing was
      // granted, in which case the next app open asks again.
      onServerAnswered: validation => forgetCoachTierPendingPurchaseIfSettled(this.auth.user?.uid, validation.error),

      // validate with coachTierValidate (writes coachTierData), branching on
      // platform exactly like the personal purchase path
      validateAsync: (response, accountId) => this.cloudFunctions.coachTierValidateAsync(
        this.buildCoachTierPurchaseParams(response, accountId, product, replacePersonalToken),
      ),

      reportValidateFailed: validation => {
        this.reportPurchaseFailure(describeRefusalReport(StorePurchaseRail.CoachTier, validation.error));
      },
      // same builder the personal purchase path uses, so a coach whose store
      // could not be reached is told the same honest thing a subscriber is
      buildValidationFailure: validation => buildStoreConfirmationFailure(validation),

      // distinct event name from the server-rejected validation above, so a
      // thrown purchase failure groups separately in Sentry
      reportThrew: (error, step) => {
        this.reportPurchaseFailure(describeThrownReport(StorePurchaseRail.CoachTier, error, step));
      },
      describeThrown: error => describeError(error),

      // re-login so the coach's new Premium grant + tier cap take effect
      finishAsync: () => this.auth.attemptCouchLoginAsync(CouchLoginReason.CoachSeatPurchaseFinish),
    });
  }

  /**
   * The confirmation request for one coach-tier PURCHASE, built in one place.
   *
   * Two callers build the identical request from the identical store response —
   * the validate step, and the device record that exists so the validate step can
   * be re-tried later. Built twice they could drift, and the field most likely to
   * be forgotten is `replacedPurchaseToken`: without it a coach whose purchase
   * replaced their personal membership recovers into paying for both.
   *
   * The iOS mapping here is the PURCHASE one. `getExistingPurchases` maps the two
   * transaction ids the other way round, so the restore path below builds its own
   * request rather than calling this — see the comment there.
   */
  private buildCoachTierPurchaseParams(
    response: { purchaseToken: string; orderId?: string; productId?: string },
    accountId: string | undefined,
    product: IAPProduct,
    replacedPurchaseToken: string | undefined,
  ): CoachTierValidateParams {
    if (isPlatformIOS()) {
      return {
        provider: 'apple',
        transactionId: response.purchaseToken,
        originalTransactionId: response.orderId || response.purchaseToken,
      };
    }
    return {
      provider: 'googlePlay',
      purchaseToken: response.purchaseToken,
      productId: response.productId || product.id,
      basePlanId: product.basePlanId,
      obfuscatedAccountId: accountId,
      // when we ran the buy as a replacement of the active personal sub, tell the
      // server which token it replaced so it retires the personal googlePlayData
      // block in the same write (V4 become-a-coach handoff)
      replacedPurchaseToken,
    };
  }

  /**
   * The store refused a coach-tier purchase because this account already owns the
   * plan. Ask the store for that purchase and record it.
   *
   * This is the coach rail's version of the Subscribe page's
   * `checkExistingStorePurchase`, and it exists for the coach whose earlier
   * purchase was charged but never recorded — a confirmation call that threw,
   * on a phone that has since been reinstalled or signed out, so the device's own
   * record of it is gone. Without this they are told to use Change Plan, which
   * they cannot use, because there is no plan recorded to change.
   *
   * It runs ONLY when this account holds no coach tier at all. A coach who really
   * does hold one gets the old answer: they are re-buying something they already
   * have, and Change Plan is the right place to send them.
   *
   * Every failure falls back to that same old answer. Nothing here is allowed to
   * turn "you already own this" into a worse message than the one it replaces.
   */
  private async confirmOwnedCoachTierAsync(product: IAPProduct): Promise<IAPPurchaseResult> {
    const alreadyOwned: IAPPurchaseResult = { success: false, error: 'already_owned' };
    if (this.auth.couch?.coachTierData) return alreadyOwned;

    try {
      const { purchases } = await BillingPlugin.getExistingPurchases();
      // the store answered about THIS product, so this is the purchase it means.
      const owned = purchases.find(p => p.productId === product.id);
      if (!owned) return alreadyOwned;

      const accountId = await this.getObfuscatedAccountId();
      const validation = await this.cloudFunctions.coachTierValidateAsync(isPlatformIOS()
        ? {
            // the restore call maps the two Apple ids the opposite way round to
            // the purchase call: here `purchaseToken` carries the ORIGINAL
            // transaction id and `orderId` the latest one. The long comment in
            // `restorePurchasesAsync` explains what mixing them up costs.
            provider: 'apple',
            transactionId: owned.orderId || owned.purchaseToken,
            originalTransactionId: owned.purchaseToken,
          }
        : {
            provider: 'googlePlay',
            purchaseToken: owned.purchaseToken,
            productId: owned.productId,
            basePlanId: product.basePlanId,
            obfuscatedAccountId: accountId,
            // no replaced token: this purchase was made in some earlier session,
            // and whether it replaced a personal subscription was decided then.
          });
      if (validation.error) {
        this.logging.trackSilentProblem('CoachTierOwnedPurchaseNotConfirmed', { error: validation.error });
        return alreadyOwned;
      }

      // the coach holds a recorded tier from here on, so nothing is pending
      forgetCoachTierPendingPurchase(this.auth.user?.uid);
      this.logging.trackSilentInfo('CoachTierRecoveredFromStore', { productId: product.id });
      await this.auth.attemptCouchLoginAsync(CouchLoginReason.CoachTierPendingRetry);
      return { success: true };
    } catch (error) {
      this.logging.trackSilentProblem('CoachTierOwnedPurchaseLookupFailed', { errorMessage: describeError(error) });
      return alreadyOwned;
    }
  }

  /** iOS only — present StoreKit's native Manage Subscriptions sheet. Works in
   *  both sandbox (TestFlight / App Review) and production, unlike the
   *  itms-apps:// production-subscriptions URL. Returns { error } on non-iOS or
   *  if the native sheet fails to present (caller falls back to the URL). */
  async showManageSubscriptionsAsync(): Promise<{ error?: string }> {
    if (!this.isAvailable() || !isPlatformIOS()) {
      return { error: 'Manage Subscriptions is only available on iOS.' };
    }
    try {
      await BillingPlugin.showManageSubscriptions();
      return {};
    } catch (error) {
      const message = describeError(error);
      this.logging.trackSilentProblem('ShowManageSubscriptionsFailed', { error: message });
      return { error: message };
    }
  }

  async restorePurchasesAsync(): Promise<IAPPurchaseResult> {
    if (!this.isAvailable()) {
      return { success: false, error: 'IAP not available on this platform.' };
    }

    // same meaning as in purchaseAsync: from the point the store hands us a
    // purchase, a failure is ours to explain and not the purchase's (#4651).
    // Here the money was taken at some earlier date rather than seconds ago,
    // but the user is equally entitled to be told their purchase exists.
    let storeHasAPurchase = false;

    try {
      const result = await BillingPlugin.getExistingPurchases();

      // confirm a purchase of one of OUR personal products, never just the first
      // one listed: the store promises no order, and a coach-tier purchase sent to
      // the personal endpoint would be recorded as personal Premium (#6242). A
      // device holding only other products has nothing to restore here.
      const personalProductIds = (isPlatformIOS()
        ? AppEnvironment.appleProductIds ?? []
        : [AppEnvironment.googlePlayProductId]
      ).filter((id): id is string => !!id);
      const pick = pickPersonalRestorePurchase(result.purchases, personalProductIds);
      const purchase = pick.chosen;
      if (!purchase) {
        return { success: false, error: 'No existing purchases found.' };
      }
      if (pick.personalCount > 1) {
        // neither store should hand us two of our personal purchases at once, so
        // say so rather than quietly confirming the first
        this.logging.trackSilentProblem('IAPRestoreSeveralPersonalPurchases', {
          count: pick.personalCount,
          productIds: result.purchases.map(p => p.productId).join(','),
        });
      }

      storeHasAPurchase = true;

      const accountId = await this.getObfuscatedAccountId();
      if (Capacitor.getPlatform() === 'ios') {
        // Swift-side asymmetry: BillingPlugin.getExistingPurchases maps
        // `purchaseToken = String(transaction.originalID)` and
        // `orderId = String(transaction.id)`, while the purchase() flow
        // maps them the other way around. So in the restore path the
        // *original* tx id is in `purchaseToken` and the *latest* tx id
        // is in `orderId` — swap them when forwarding to the server,
        // which expects Apple's nomenclature (originalTransactionId =
        // the subscription-line identifier used for ownership lookup).
        // Without this swap, `checkStoreTokenOwnership` queries CouchDB
        // for a non-existent originalTransactionId and falsely reports
        // alreadyLinked: false — allowing the same Apple sub to be
        // linked to multiple users.
        const validation = await this.cloudFunctions.appleValidateAsync({
          transactionId: purchase.orderId || purchase.purchaseToken,
          originalTransactionId: purchase.purchaseToken,
          signedTransaction: purchase.signedTransaction,
        });
        if (validation.error) {
          return buildStoreConfirmationFailure(validation);
        }
      } else {
        const validation = await this.cloudFunctions.googlePlayValidateAsync({
          purchaseToken: purchase.purchaseToken,
          productId: purchase.productId,
          obfuscatedAccountId: accountId,
        });
        if (validation.error) {
          return buildStoreConfirmationFailure(validation);
        }
      }

      await this.auth.attemptCouchLoginAsync(CouchLoginReason.StoreRestore);
      return { success: true };
    } catch (error) {
      // the network tag matches the purchase path's thrown reports (#6244), so
      // a phone with no connection can be filtered out in Sentry
      this.logging.trackSilentProblem('IAP restore error', error, { network: isNetworkError(error) ? 'yes' : 'no' });
      return {
        success: false,
        error: describeError(error),
        confirmationFailedAfterPurchase: storeHasAPurchase,
        alreadyReported: true,
      };
    }
  }

  async getObfuscatedAccountId(): Promise<string | undefined> {
    const uid = this.auth.user?.uid;
    if (!uid) return undefined;
    const encoded = new TextEncoder().encode(uid);
    const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  private calculateSavings(products: IAPProduct[]) {
    const monthly = products.find(p => p.numMonths === 1);
    const yearly = products.find(p => p.numMonths === 12);
    if (!monthly || !yearly) return;

    const monthlyAnnualCost = monthly.priceMicros * 12;
    const savingMicros = monthlyAnnualCost - yearly.priceMicros;
    if (savingMicros <= 0) return;

    // format using the yearly product's storefront locale so the derived
    // strings match priceText's format exactly (no "US$X.YZ" mixed-locale
    // output when storefront and device locale disagree — Apple 3.1.2(c)
    // adjacent: keeps the savings element subordinate yet locale-consistent)
    yearly.preDiscount = this.formatMicros(monthlyAnnualCost, yearly.currencyCode, yearly.priceLocale);
    yearly.saving = this.formatMicros(savingMicros, yearly.currencyCode, yearly.priceLocale);
  }

  // Format a price (in micros) so the output matches StoreKit's displayPrice
  // formatting (symbol, decimal separator, position). Apple's formatter
  // follows the storefront REGION's conventions (e.g. "24,99 €" for Croatia
  // regardless of device language), while JS Intl.NumberFormat follows the
  // LANGUAGE conventions by default — so passing the raw Apple locale
  // `en_HR@currency=EUR` would give us "49.89 EUR" while displayPrice shows
  // "49,99 €" for the same currency.
  //
  // We mirror Apple's behavior by extracting the region and asking
  // Intl.Locale.maximize() for the region's likely-primary locale
  // (e.g. HR -> hr-Latn-HR). Falls back to navigator.language when no
  // priceLocale is supplied (Android Billing library doesn't expose one).
  // Surfaces Intl errors via trackSilentProblem so a future runtime regression
  // (unsupported locale, malformed currency code) is visible in Sentry.
  private formatMicros(micros: number, currencyCode: string, locale?: string): string {
    const amount = micros / 1_000_000;
    const resolvedLocale = this.canonicalizeLocaleForCurrencyDisplay(locale ?? navigator.language ?? 'en-US');
    try {
      return new Intl.NumberFormat(resolvedLocale, {
        style: 'currency',
        currency: currencyCode,
      }).format(amount);
    } catch (err) {
      this.logging.trackSilentProblem('IAPFormatMicrosIntlError', {
        resolvedLocale,
        currencyCode,
        errorMessage: describeError(err),
      });
      return `${amount.toFixed(2)} ${currencyCode}`;
    }
  }

  // Apple's Locale identifiers use `_` separators and `@key=value` extensions
  // (e.g. "en_HR@currency=EUR"); strip those, then expand the region into its
  // likely primary locale so currency formatting matches displayPrice.
  // Intl.Locale parse failures surface via trackSilentProblem rather than
  // silently regressing to the cleaned string — a future Apple locale shape
  // change should be visible to us.
  private canonicalizeLocaleForCurrencyDisplay(rawLocale: string): string {
    const cleaned = rawLocale.split('@')[0].replace(/_/g, '-');
    try {
      const parsed = new Intl.Locale(cleaned);
      if (parsed.region) {
        return new Intl.Locale(`und-${parsed.region}`).maximize().baseName;
      }
      return cleaned;
    } catch (err) {
      this.logging.trackSilentProblem('IAPCanonicalizeLocaleParseError', {
        rawLocale,
        cleaned,
        errorMessage: describeError(err),
      });
      return cleaned;
    }
  }
}

function billingPeriodToMonths(period: string): number {
  // ISO 8601 durations: P1M = 1 month, P1Y = 12 months, P3M = 3 months
  const match = period.match(/P(\d+)([MY])/);
  if (!match) return 1;
  const num = parseInt(match[1], 10);
  return match[2] === 'Y' ? num * 12 : num;
}
